import React, { useEffect, useState } from "react";
import {
  ShieldCheck,
  Plus,
  Trash2,
  FileSpreadsheet,
  X,
  Edit2,
  FilePenLine,
  CheckCircle,
  Clock,
  RefreshCw,
  FileDown,
  CalendarDays,
  BriefcaseBusiness,
  Save,
  Ban,
  ArrowRightLeft,
  PieChart,
  File,
  UploadCloud,
  UserCheck,
  Lock,
  Icon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  updateCompanyLeaveConfiguration,
  fetchCompanyLeaveConfiguration,
  deleteLeaveConfiguration,
  updateLeaveConfig,
  fetchHolidays,
  addHolidays,
  deleteHoliday,
  addHolidayBulk,
  editHoliday,
  copyHolidaysFromPreviousYear,
} from "@/services/adminService";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Dialog,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";

/* -------------------- FORM -------------------- */

export function SectionHeader({ title, accent = "primary" }) {
  const accentClasses =
    accent === "emerald"
      ? "border-emerald-200 dark:border-emerald-500"
      : accent === "amber"
      ? "border-amber-200 dark:border-amber-500"
      : accent === "indigo"
      ? "border-indigo-200 dark:border-indigo-500"
      : "border-slate-200 dark:border-slate-700";

  return (
    <>
      <div className={`${accentClasses} flex items-center gap-2 border-l-4 pl-3`}>
        <h4 className="text-sm font-semibold tracking-tight text-slate-800 dark:text-slate-100">
          {title}
        </h4>
      </div>
      <Separator />
    </>
  );
}

/**
 * Locked overlay (UI only)
 */
export function LockedOverlay() {
  return (
    <div
      className="absolute inset-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur-[1px]
      rounded-2xl flex items-center justify-center"
    >
      <div className="text-center text-sm text-slate-700 dark:text-slate-200 space-y-1 px-6">
        <Lock className="mx-auto" size={18} />
        <p className="font-medium">Upgrade required</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Upgrade to configure leave rules & limits.
        </p>
      </div>
    </div>
  );
}

/**
 * Card wrapper for rules (UI only)
 */
export function RuleCard({ children }) {
  return (
    <div
      className="rounded-2xl border p-5 space-y-5
      bg-white dark:bg-slate-900
      border-slate-200 dark:border-slate-800"
    >
      {children}
    </div>
  );
}

/**
 * Single rule row (UI only)
 */
export function RuleRow({ icon: Icon, title, description, checked, onChange }) {
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 text-slate-500 dark:text-slate-400">
            <Icon size={18} />
          </div>

          <div className="space-y-0.5">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {title}
            </p>
            <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              {description}
            </p>
          </div>
        </div>

        <Switch checked={checked} onCheckedChange={onChange} />
      </div>
    </>
  );
}

const LeavePolicyForm = ({
  initialData,
  isOpen,
  onSave,
  onCancel,
  hasAdvancedLeave,
}) => {
  const defaultFormData = {
    name: "",
    code: "",
    type: "Paid",
    accrualType: "yearly",
    count: 0,
    carryForward: false,
    carryForwardLimit: 0,
    proRata: true,
    allowOnProbation: false,
    autoApplyAfterProbation: false,
    description: "",
    _id: undefined,
    is_encashable: false,
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({ name: "", code: "", count: "" });

  useEffect(() => {
    if (!isOpen) return;
    if (!initialData) {
      setFormData(defaultFormData);
      return;
    }

    setFormData({
      name: initialData.leave_name || "",
      code: initialData.leave_code || "",
      type: initialData.leave_type === "paid" ? "Paid" : "Unpaid",
      accrualType:
        initialData.accrual_method === "monthly" ? "Monthly" : "yearly",
      count: initialData.accrual_days ?? 0,
      carryForward: !!initialData.is_carry_forward,
      carryForwardLimit: initialData.carry_forward_days ?? 0,
      proRata: !!initialData.is_pro_rata,
      allowOnProbation: !!initialData.is_allow_probation,
      autoApplyAfterProbation: !!initialData.auto_apply_after_probation,
      description: initialData.description || "",
      _id: initialData._id,
      is_encashable: initialData.is_encashable,
    });
  }, [initialData, isOpen]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // "Auto Apply After Probation" is only relevant when the leave is NOT
      // usable during probation. If Probation Eligibility is enabled, force
      // Auto Apply After Probation off (and the toggle is hidden in the UI).
      if (field === "allowOnProbation" && value) {
        next.autoApplyAfterProbation = false;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Policy name is required.";
    if (!formData.code.trim()) newErrors.code = "Policy code is required.";
    if (!formData.count || Number(formData.count) <= 0)
      newErrors.count = "Leave days must be more than 0.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // toast.error("Please fill all required fields");
      return;
    }

    const payload = {
      name: formData.name.trim(),
      code: formData.code.trim(),
      type: formData.type,
      accrualType: formData.accrualType,
      count: Number(formData.count) || 0,
      carryForward: formData.carryForward,
      carryForwardLimit: formData.carryForward
        ? Number(formData.carryForwardLimit) || 0
        : 0,
      is_encashable: formData.is_encashable,
      proRata: formData.proRata,
      allowOnProbation: formData.allowOnProbation,
      autoApplyAfterProbation: formData.autoApplyAfterProbation,
    };

    if (!hasAdvancedLeave) {
      Object.assign(payload, {
        accrualType: "yearly",
        carryForward: false,
        carryForwardLimit: 0,
        proRata: false,
        allowOnProbation: true,
        autoApplyAfterProbation: false,
      });
    }

    try {
      let res;
      if (!formData._id) {
        // CREATE
        res = await updateCompanyLeaveConfiguration(payload);
      } else {
        // EDIT
        res = await updateLeaveConfig(formData._id, payload);
      }

      const updatedList = res?.leave_config || res?.data?.leave_config;
      if (Array.isArray(updatedList)) {
        onSave(updatedList);
        toast.success("Policy saved successfully!");
        onCancel();
      }
    } catch (err) {
      console.error("Failed to update leave configuration", err);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        className="max-w-3xl p-0 rounded-2xl overflow-hidden
        bg-white dark:bg-slate-900
        border border-slate-200 dark:border-slate-800
        shadow-2xl"
      >
        {/* HEADER */}
        <div
          className="px-8 py-6 border-b
          bg-slate-50 dark:bg-slate-800/60
          border-slate-200 dark:border-slate-800"
        >
          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            {formData?._id ? "Edit Leave Policy" : "Create New Policy"}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Define how employees accrue and use their time off.
          </p>
        </div>

        <DialogDescription className="p-0">
          <div className="px-8 py-6 max-h-[60vh] overflow-y-auto space-y-10">
            {/* IDENTITY */}
            <section className="space-y-5">
              <SectionHeader
                title="Identity & Classification"
                accent="indigo"
              />

              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-12 md:col-span-8">
                  <Label>Policy Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="h-11"
                  />
                </div>

                <div className="col-span-12 md:col-span-4">
                  <Label>Short Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) =>
                      handleChange("code", e.target.value.toUpperCase())
                    }
                    className="h-11 text-center font-mono tracking-widest"
                  />
                </div>
              </div>

              {/* Paid / Unpaid */}
              <div
                className="flex rounded-xl p-1 border
                bg-slate-100 dark:bg-slate-800
                border-slate-200 dark:border-slate-700"
              >
                {["Paid", "Unpaid"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleChange("type", type)}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition
                      ${
                        formData.type === type
                          ? "bg-white dark:bg-slate-700 shadow text-primary"
                          : "text-slate-500 hover:text-slate-700 dark:text-slate-400"
                      }`}
                  >
                    {type} Leave
                  </button>
                ))}
              </div>
            </section>

            {/* ACCRUAL */}
            <section className="space-y-5">
              <SectionHeader title="Accrual Strategy" accent="emerald" />

              <div className="grid md:grid-cols-2 gap-4">
                <RadioCard
                  selected={formData.accrualType === "yearly"}
                  onClick={() => handleChange("accrualType", "yearly")}
                  title="Fixed Annual"
                  description="Full balance available instantly"
                  icon={CalendarDays}
                />

                <RadioCard
                  selected={formData.accrualType === "Monthly"}
                  onClick={() =>
                    hasAdvancedLeave && handleChange("accrualType", "Monthly")
                  }
                  title="Monthly Earned"
                  description={
                    hasAdvancedLeave ? "Accrues monthly" : "Upgrade Required"
                  }
                  icon={RefreshCw}
                  disabled={!hasAdvancedLeave}
                />
              </div>

              {/* Allocation Card */}
              <div
                className="rounded-2xl border p-6 flex flex-col md:flex-row gap-8
                bg-slate-50 dark:bg-slate-900
                border-slate-200 dark:border-slate-800"
              >
                <div className="flex-1">
                  <Label>
                    {formData.accrualType === "yearly"
                      ? "Annual Allocation"
                      : "Monthly Accrual"}
                  </Label>

                  <div className="relative">
                    <Input
                      type="number"
                      value={formData.count}
                      onChange={(e) =>
                        handleChange("count", Number(e.target.value) || 0)
                      }
                      onWheel={(e) => e.currentTarget.blur()}
                      className="h-14 text-2xl font-bold [&::-webkit-inner-spin-button]:appearance-none
                        text-slate-900 dark:text-white"
                    />
                    <span
                      className="absolute right-4 top-1/2 -translate-y-1/2
                      text-slate-400 text-sm font-semibold"
                    >
                      Days
                    </span>
                  </div>
                </div>

                <div className="flex-1">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                    Yearly Forecast
                  </p>
                  <p className="text-4xl font-black text-primary">
                    {formData.accrualType === "yearly"
                      ? formData.count
                      : (formData.count * 12).toFixed(1)}
                    <span className="text-xs ml-2 text-slate-500">Days</span>
                  </p>
                </div>
              </div>
            </section>

            {/* RULES */}
            <section className="relative space-y-5">
              {!hasAdvancedLeave && <LockedOverlay />}

              <SectionHeader title="Governance Rules" accent="amber" />

              <RuleCard>
                <div className="">
                  <RuleRow
                    icon={ArrowRightLeft}
                    title="Carry Forward"
                    description="Roll over unused balance"
                    checked={formData.carryForward}
                    onChange={(v) => handleChange("carryForward", v)}
                  />
                  {formData.carryForward && (
                    <div
                      className="mt-3 ml-8 p-3 rounded-lg
                    bg-slate-50 dark:bg-slate-900/60
                    border border-slate-200 dark:border-slate-800"
                    >
                      <div className="flex items-center justify-between gap-3 m-1">
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-200 whitespace-nowrap">
                          Allow Leave Encashment
                        </label>

                        <div className="relative">
                          <Switch
                            checked={formData.is_encashable}
                            onCheckedChange={(v) =>
                              handleChange("is_encashable", v)
                            }
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3 m-1">
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-200 whitespace-nowrap">
                          Max Days to Carry :
                        </label>

                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            onWheel={(e) => e.currentTarget.blur()}
                            disabled={!hasAdvancedLeave}
                            className={`w-24 border rounded-md px-2 py-1 text-sm outline-none [&::-webkit-inner-spin-button]:appearance-none
                            bg-white dark:bg-slate-900
                            text-slate-700 dark:text-slate-100
                            ${
                              hasAdvancedLeave
                                ? "border-slate-300 dark:border-slate-700 focus:border-primary"
                                : "border-slate-200 dark:border-slate-800 cursor-not-allowed opacity-60"
                            }
                          `}
                            value={formData.carryForwardLimit}
                            onChange={(e) =>
                              handleChange(
                                "carryForwardLimit",
                                Number(e.target.value) || 0,
                              )
                            }
                          />

                          {!hasAdvancedLeave && (
                            <span className="absolute -top-6 left-0 text-xs text-amber-600 dark:text-amber-400">
                              Upgrade to edit carry forward rules
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <RuleRow
                  icon={PieChart}
                  title="Pro-rata Allocation"
                  description="Based on joining date"
                  checked={formData.proRata}
                  onChange={(v) => handleChange("proRata", v)}
                />
                <RuleRow
                  icon={UserCheck}
                  title="Probation Eligibility"
                  description="Usable during probation"
                  checked={formData.allowOnProbation}
                  onChange={(v) => handleChange("allowOnProbation", v)}
                />
                {!formData.allowOnProbation && (
                  <RuleRow
                    icon={UserCheck}
                    title="Auto Apply After Probation"
                    description="Automatically assign this leave type to employees after their probation period is completed."
                    checked={formData.autoApplyAfterProbation}
                    onChange={(v) =>
                      handleChange("autoApplyAfterProbation", v)
                    }
                  />
                )}
              </RuleCard>
            </section>
          </div>
        </DialogDescription>

        {/* FOOTER */}
        <DialogFooter
          className="px-8 pb-4
          flex justify-between"
        >
          <Button
            variant="ghost"
            className="h-11 hover:bg-transparent hover:text-foreground"
            onClick={onCancel}
          >
            Discard
          </Button>

          <Button onClick={handleSubmit} className="h-11 px-8 font-bold">
            <Save size={16} />
            {formData?._id ? "Update Policy" : "Create Policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* -------------------- RADIO CARD -------------------- */

const RadioCard = ({
  selected,
  onClick,
  title,
  description,
  icon: Icon,
  disabled = false,
}) => (
  <div
    onClick={!disabled ? onClick : undefined}
    title={disabled ? "Upgrade to unlock this feature" : ""}
    className={`p-4 rounded-xl border-2 flex items-start gap-4 transition-all
      ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : "cursor-pointer hover:border-primary/60"
      }
      ${selected ? "border-primary" : "border-slate-200"}
    `}
  >
    <div
      className={`p-2 rounded-lg shrink-0 ${
        selected ? "bg-primary text-white" : "bg-transparent text-slate-400"
      }`}
    >
      <Icon size={20} />
    </div>

    <div>
      <h4
        className={`text-sm font-bold ${
          selected ? "text-primary" : "text-foreground"
        }`}
      >
        {title}
      </h4>
      <p className="text-xs text-slate-500 mt-1 leading-relaxed">
        {description}
      </p>
    </div>

    <div
      className={`ml-auto w-4 h-4 rounded-full border-2 flex items-center justify-center mt-1 ${
        selected ? "border-primary" : "border-slate-300"
      }`}
    >
      {selected && <div className="w-2 h-2 bg-primary rounded-full" />}
    </div>
  </div>
);

/* -------------------- MAIN CONFIG COMPONENT -------------------- */

const LeaveConfiguration = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [companyHolidays, setCompanyHolidays] = useState([]);
  const [editingHoliday, setEditingHoliday] = useState();
  const [holidayModal, setIsHolidayModal] = useState(false);
  const [showHolidayConfirm, setShowHolidayConfirm] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [excelModal, setExcelModal] = useState(false);
  const [activeTab, setActiveTab] = useState("leave");
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const { hasFeature, isPaidOnline } = useSubscription();
  const { can } = usePermission();
  const { canView, canCreate, canEdit, canDelete } = createCanHelpers(can);
  const canViewLeaveComponent = canView("settings_leave_configuration");
  const canCreateLeaveComponent = canCreate("settings_leave_configuration");
  const canEditLeaveComponent = canEdit("settings_leave_configuration");
  const canDeleteLeaveComponent = canDelete("settings_leave_configuration");

  const canViewHolidayComponent = canView("holiday_configuration");
  const canCreateHolidayComponent = canCreate("holiday_configuration");
  const canEditHolidayComponent = canEdit("holiday_configuration");
  const canDeleteHolidayComponent = canDelete("holiday_configuration");

  const hasAdvancedLeave = hasFeature("custom_leave_management");
  const navigate = useNavigate();

  const fetchLeaveConfigurations = async () => {
    try {
      const { data: res } = await fetchCompanyLeaveConfiguration();
      console.log(res);
      const list = res?.leave_config || [];
      if (Array.isArray(list)) setPolicies(list);
    } catch (err) {
      console.error("Failed to fetch leave configuration", err);
    }
  };

  const filteredHolidays = companyHolidays.filter((h) => {
    const year = new Date(h.date).getFullYear();
    return year === selectedYear;
  });

  const handleFetchHolidays = async () => {
    try {
      const res = await fetchHolidays();
      if (res) setCompanyHolidays(res);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to fetch holidays"));
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      if (isMounted) setInitialLoading(true);
      try {
        await Promise.all([fetchLeaveConfigurations(), handleFetchHolidays()]);
      } finally {
        if (isMounted) setInitialLoading(false);
      }
    };
    init();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleAddLeavePolicy = () => {
    if(!hasAdvancedLeave) {
      navigate(`/subscription`, {
        state: {
          reason: "feature_upgrade_required",
        }
      });
      return;
    };
    setEditingPolicy(null);
    setIsModalOpen(true);
  };

  const handleEdit = async (policy) => {
    setEditingPolicy(policy);
    setIsModalOpen(true);
  };

  const handleAddHoliday = () => {
    setEditingHoliday(null);
    setIsHolidayModal(true);
  };

  const handleHolidayEdit = async (holiday) => {
    setEditingHoliday(holiday);
    setIsHolidayModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteLeaveConfiguration(id);
      toast.success("Leave policy deleted");
      fetchLeaveConfigurations();
    } catch (err) {
      toast.error("Failed to delete policy");
      console.error("Delete failed:", err);
    } finally {
      setShowConfirm(false);
      setDeleteId(null);
    }
  };

  // Computed Stats (backend shape)
  const totalPaidLeaves = policies?.filter((p) => p.leave_type === "paid").reduce((acc, curr) => {
      const annualValue =
        curr.accrual_method === "yearly"
          ? curr.accrual_days
          : curr.accrual_days * 12;

      return acc + annualValue;
    }, 0);

  const carryForwardCount = policies.filter(
    (p) => p.is_carry_forward === true
  ).length;

  const handleHoliday = async (holiday) => {
    try {
      if (!holiday.id) {
        const res = await addHolidays(holiday);
        if (res) handleFetchHolidays();
        return;
      }
      const res = await editHoliday(holiday.id, holiday);
      if (res) handleFetchHolidays();
      return;
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to save holiday"));
      }
    }
  };

  const handleHolidayDelete = async () => {
    if (!selectedHoliday?._id) return;
    try {
      const res = await deleteHoliday(selectedHoliday._id);
      if (res) handleFetchHolidays();
      setShowHolidayConfirm(false);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to delete holiday"));
      }
    }
  };

  const handleExcelUpload = async (file) => {
    try {
      const res = await addHolidayBulk(file);
      if (res) handleFetchHolidays();
      setExcelModal(false);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to upload holiday file"));
      }
    }
  };

  const downloadHolidayFileHelper = (arrayBuffer) => {
    const blob = new Blob([arrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `new_holidays_${selectedYear}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  };

  const downloadFromLastYear = async () => {
    try {
      const lastYearHolidays = companyHolidays.filter((h) => {
        const year = new Date(h.date).getFullYear();
        return year === selectedYear - 1;
      });

      if (lastYearHolidays.length === 0) {
        toast.error(`No holidays found for ${selectedYear - 1}`);
        setShowCopyModal(false);
        return;
      }
      const res = await copyHolidaysFromPreviousYear(selectedYear - 1, false);
      downloadHolidayFileHelper(res.data);
    } catch (error) {
      console.error(error);
      toast.error(`${error.response.data.error}`);
    }
  };

  const copyToThisYear = async () => {
    try {
      const lastYearHolidays = companyHolidays.filter((h) => {
        const year = new Date(h.date).getFullYear();
        return year === selectedYear - 1;
      });

      if (lastYearHolidays.length === 0) {
        toast.error(`No holidays found for ${selectedYear - 1}`);
        setShowCopyModal(false);
        return;
      }
      const res = await copyHolidaysFromPreviousYear(selectedYear - 1, true);

      if (!res || !res.data) {
        toast.error("Unexpected response from server.");
        return;
      }

      if (res.data.data?.length === 0) {
        toast.error(res.data.message || "No holidays found to copy.");
        return;
      }

      await handleFetchHolidays();
      setShowCopyModal(false);
    } catch (error) {
      console.error(error);
      toast.error(`${error.response.data.error}`);
    }
  };

  if (initialLoading) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border p-6 space-y-4">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-96" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </div>
        <Skeleton className="h-10 w-60" />
        <div className="rounded-2xl border p-6 space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header Stats */}
      <div
        className="rounded-2xl p-6 shadow-sm relative overflow-hidden bg-gradient-to-r from-slate-50 to-slate-100 text-slate-800 border border-border 
  dark:bg-gradient-to-r dark:from-slate-800 dark:to-slate-900 dark:text-white"
      >
        <div className="absolute right-0 top-0 h-full w-64 transform skew-x-12 translate-x-20"></div>
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <h3 className="text-xl font-bold mb-1">
              {activeTab === "leave"
                ? "Leave Policy Configuration"
                : "Holiday Configuration"}
            </h3>
            <p className="text-slate-400 text-sm">
              {activeTab === "leave"
                ? "Define and manage time-off rules for your organization."
                : "Manage official holidays for your organization."}
            </p>
          </div>

          {activeTab === "leave" && (
            <div className="flex gap-8">
              <div className="text-center">
                <p className="text-3xl font-bold">{policies.length}</p>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mt-1">
                  Policy Types
                </p>
              </div>

              <div className="w-px h-12 bg-slate-200 dark:bg-slate-700"></div>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">
                  {totalPaidLeaves}
                </p>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mt-1">
                  Avg. Annual Days
                </p>
              </div>
              <div className="w-px h-12 bg-slate-200 dark:bg-slate-700"></div>
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-400">
                  {carryForwardCount}
                </p>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mt-1">
                  Allow Carry Fwd
                </p>
              </div>
            </div>
          )}

          {activeTab === "holidays" && (
            <div className="flex gap-8">
              <div className="w-px h-12 bg-slate-200 dark:bg-slate-700"></div>
              <div className="text-center">
                <p className="text-3xl font-bold text-indigo-500">
                  {filteredHolidays.length}
                </p>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mt-1">
                  Total Holidays
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Header + Add */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex justify-start space-x-8 overflow-x-auto bg-transparent h-auto p-0 no-scrollbar">
          {canViewLeaveComponent && (
            <TabsTrigger value="leave">Leaves</TabsTrigger>
          )}
          {canViewHolidayComponent && (
            <TabsTrigger value="holidays">Holidays</TabsTrigger>
          )}
        </TabsList>
        {canViewLeaveComponent && (
          <TabsContent value="leave">
            <div className="flex justify-between items-center pt-2">
              <h4 className="font-bold text-lg dark:text-white">
                Active Policies
              </h4>
              <Button
                variant="primary"
                disabled={!isPaidOnline && !hasAdvancedLeave}
                onClick={() => {
                  if (!canCreate("settings_leave_configuration", true)) return;
                  handleAddLeavePolicy();
                }}
                className={`text-primary border border-primary mb-4 ${
                  !canCreateLeaveComponent
                    ? `opacity-70 cursor-not-allowed`
                    : ""
                }`}
              >
                <Plus size={18} /> Create New Policy
                {!hasAdvancedLeave && <Lock className="w-4 h-4 ml-1" />}
              </Button>
            </div>
            {/* Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {policies.map((policy) => {
                const id = policy._id || policy.id;
                const isLockedUnpaid =
                  policy.leave_type === "unpaid" && policy.is_system_generated;
                const canDeleteLeave = hasAdvancedLeave && !isLockedUnpaid;

                return (
                  <div
                    key={id}
                    className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300 group overflow-hidden flex flex-col relative"
                  >
                    {/* Color Strip */}
                    <div
                      className={`h-1.5 w-full ${
                        policy.leave_type === "paid"
                          ? "bg-emerald-500"
                          : policy.leave_type === "unpaid"
                            ? "bg-amber-500"
                            : "bg-purple-500"
                      }`}
                    ></div>

                    <div className="p-5 flex-1">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold ${
                              policy.leave_type === "paid"
                                ? "bg-emerald-50 text-primary dark:bg-emerald-500/15 dark:text-emerald-300"
                                : policy.leave_type === "unpaid"
                                  ? "bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300"
                                  : "bg-purple-50 text-purple-600 dark:bg-purple-500/15 dark:text-purple-300"
                            }`}
                          >
                            {policy.leave_code}
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-800 dark:text-slate-100 text-base">
                              {policy.leave_name}
                            </h4>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {policy.leave_type === "paid" ? "Paid" : "Unpaid"}{" "}
                              Leave
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            disabled={isLockedUnpaid}
                            onClick={() => {
                              if (
                                !canEdit(
                                  "settings_leave_configuration",
                                  true,
                                ) ||
                                isLockedUnpaid
                              )
                                return;
                              handleEdit(policy);
                            }}
                            title={
                              isLockedUnpaid
                                ? "System-defined unpaid leave cannot be edited"
                                : "Edit policy"
                            }
                            className={`p-2 rounded-full transition-colors text-slate-400 hover:text-primary hover:bg-slate-50 dark:text-slate-300 dark:hover:text-[#6ad5df] dark:hover:bg-slate-800
                            ${
                              isLockedUnpaid || !canEditLeaveComponent
                                ? "opacity-70 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            <Edit2 size={16} />
                          </button>
                          {canDeleteLeave && (
                            <button
                              onClick={() => {
                                if (
                                  !canDelete(
                                    "settings_leave_configuration",
                                    true,
                                  )
                                )
                                  return;
                                setDeleteId(id);
                                setShowConfirm(true);
                              }}
                              className={`p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:text-slate-300 dark:hover:text-red-400 dark:hover:bg-red-900/30 rounded-full transition-colors ${
                                !canDeleteLeaveComponent
                                  ? `opacity-70 cursor-not-allowed`
                                  : ``
                              }`}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 py-4 border-t border-b border-slate-100 dark:border-slate-800 border-dashed">
                        <div>
                          <p className="text-xs text-slate-400 dark:text-slate-500 font-semibold uppercase mb-1">
                            Allocation
                          </p>
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-200 flex items-center gap-1">
                            {policy.accrual_method === "yearly" ? (
                              <CalendarDays
                                size={14}
                                className="text-primary"
                              />
                            ) : (
                              <RefreshCw size={14} className="text-primary" />
                            )}
                            {policy.accrual_method === "yearly"
                              ? "yearly"
                              : policy.accrual_method === "monthly"
                                ? "Monthly"
                                : "Restrict"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 dark:text-slate-500 font-semibold uppercase mb-1">
                            Quota
                          </p>
                          <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                            {policy.leave_type === "unpaid" &&
                            policy.is_system_generated
                              ? "Unlimited"
                              : policy.accrual_days}
                            {policy.leave_type !== "unpaid" && (
                              <span className="text-slate-400 text-xs">
                                {policy.accrual_method === "yearly"
                                  ? " / Year"
                                  : " / Month"}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center gap-2 text-xs">
                        {policy.is_carry_forward ? (
                          <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 font-medium flex items-center gap-1">
                            <CheckCircle size={12} /> Carries Forward (
                            {policy.carry_forward_days})
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300 font-medium flex items-center gap-1">
                            <Ban size={12} /> No Carry Fwd
                          </span>
                        )}

                        {policy.is_pro_rata && (
                          <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300 font-medium">
                            Pro-rata
                          </span>
                        )}

                        {!policy.is_allow_probation && (
                          <span className="px-2 py-1 rounded bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 font-medium flex items-center gap-1">
                            <UserCheck size={12} /> No Probation
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {hasAdvancedLeave && (
                <button
                  disabled={!canCreateLeaveComponent}
                  onClick={() => {
                    if (!canCreateLeaveComponent) return;
                    handleAddLeavePolicy();
                  }}
                  className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-6 flex flex-col items-center justify-center text-slate-400 dark:text-slate-300 hover:border-primary hover:text-primary hover:bg-primary/5 dark:hover:bg-slate-800 transition-all duration-200 group min-h-[220px]"
                >
                  <div className="w-14 h-14 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-4 group-hover:bg-white dark:group-hover:bg-slate-700 group-hover:scale-110 transition-transform shadow-sm">
                    <Plus size={24} />
                  </div>
                  <span className="font-bold text-lg">New Policy</span>
                  <span className="text-xs mt-1 text-slate-400 dark:text-slate-500">
                    Click to configure
                  </span>
                </button>
              )}
            </div>
          </TabsContent>
        )}
        {canViewHolidayComponent && (
          <TabsContent value="holidays">
            <div>
              <div className="flex justify-between items-center pt-2">
                {/* LEFT SIDE — SINGLE DROPDOWN */}
                <Select
                  value={selectedYear.toString()}
                  onValueChange={(y) => setSelectedYear(Number(y))}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Select Year" className="text-sm">
                      {selectedYear}
                    </SelectValue>
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value={(currentYear + 1).toString()}>
                      {currentYear + 1}
                    </SelectItem>

                    <SelectItem value={currentYear.toString()}>
                      {currentYear}
                    </SelectItem>

                    <SelectItem value={(currentYear - 1).toString()}>
                      {currentYear - 1}
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* RIGHT SIDE ACTION BUTTONS */}
                {selectedYear >= currentYear && (
                  <div className="flex justify-end gap-x-4">
                    <Button
                      variant="primary"
                      disabled={!canCreateHolidayComponent}
                      onClick={() => {
                        if (!canCreateHolidayComponent) return;
                        setExcelModal(true);
                      }}
                      className={` text-primary border border-primary mb-4 ${
                        !canCreateHolidayComponent
                          ? "opacity-70 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <File size={18} /> Upload File
                    </Button>
                    <Button
                      variant="primary"
                      disabled={!canCreateHolidayComponent}
                      onClick={() => {
                        if (!canCreateHolidayComponent) return;
                        handleAddHoliday();
                      }}
                      className={`text-primary border border-primary mb-4 ${
                        !canCreateHolidayComponent
                          ? "opacity-70 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <Plus size={18} /> Add Holiday
                    </Button>
                  </div>
                )}
              </div>

              <div>
                {filteredHolidays.length === 0 ? (
                  <div className="py-20 flex flex-col items-center justify-center text-center text-slate-500 dark:text-slate-400">
                    <CalendarDays
                      size={48}
                      className="mb-4 text-primary opacity-80"
                    />

                    <p className="text-lg font-semibold mb-2">
                      No holidays found for {selectedYear}
                    </p>
                    {selectedYear >= currentYear && (
                      <div className="flex items-center gap-4 mt-3">
                        <Button
                          disabled={!canCreateHolidayComponent}
                          onClick={() => {
                            if (!canCreateHolidayComponent) return;
                            setShowCopyModal(true);
                          }}
                          variant="outline"
                          className={`border-slate-300 text-slate-700  dark:text-slate-200  dark:border-slate-700 hover:bg-primary/10  hover:text-primary  dark:hover:bg-primary/20 dark:hover:text-primary ${
                            !canCreateHolidayComponent
                              ? "opacity-70 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          Copy from last year
                        </Button>

                        <Button
                          onClick={() => {
                            downloadFromLastYear();
                          }}
                          variant="ghost"
                          className="text-primary flex items-center gap-2"
                        >
                          <FileDown size={16} />
                          Download holidays from last year (.xlsx)
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-primary/10 rounded-sm">
                        <TableHead>Name</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="dark:text-white">
                      {filteredHolidays.map((holiday) => {
                        const isPast =
                          holiday.date && new Date(holiday.date) < new Date();
                        return (
                          <TableRow key={holiday._id}>
                            <TableCell>{holiday.name}</TableCell>
                            <TableCell>
                              {new Date(holiday.date).toDateString()}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {/* EDIT */}
                                <Button
                                  disabled={isPast || !canEditHolidayComponent}
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    if (!canEditHolidayComponent || isPast)
                                      return;
                                    handleHolidayEdit(holiday);
                                  }}
                                  className={` text-primary  bg-primary/20  rounded-md  transition-all p-2 ${
                                    !canEditHolidayComponent || isPast
                                      ? "opacity-70 cursor-not-allowed"
                                      : ""
                                  }`}
                                >
                                  <FilePenLine size={16} />
                                </Button>

                                {/* DELETE */}
                                <Button
                                  disabled={isPast}
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    if (canDeleteHolidayComponent) {
                                      setSelectedHoliday(holiday);
                                      setShowHolidayConfirm(true);
                                    }
                                  }}
                                  className={` text-red-500 bg-red-500/20  rounded-md transition-all  p-2 ${
                                    !canDeleteHolidayComponent
                                      ? "opacity-70 cursor-not-allowed"
                                      : ""
                                  }`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <LeavePolicyForm
        isOpen={isModalOpen}
        initialData={editingPolicy}
        hasAdvancedLeave={hasAdvancedLeave}
        onSave={() => {
          fetchLeaveConfigurations();
          setIsModalOpen(false);
        }}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingPolicy(null);
        }}
      />

      {showConfirm && (
        <Dialog
          open={showConfirm}
          onOpenChange={(open) => {
            if (!open) {
              setShowConfirm(false);
              setDeleteId(null);
            }
          }}
        >
          <DialogContent className="max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-slate-800 dark:text-white">
                Delete Leave Policy?
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-600 dark:text-slate-400">
                Are you sure you want to delete this leave policy? This action
                cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                className="dark:border-slate-700"
                onClick={() => {
                  setShowConfirm(false);
                  setDeleteId(null);
                }}
              >
                Cancel
              </Button>

              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleDelete(deleteId)}
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <ConfirmModal
        open={showHolidayConfirm}
        onClose={() => setShowHolidayConfirm(false)}
        onConfirm={handleHolidayDelete}
        destructive={true}
        title="Delete Holiday?"
        description={`Are you sure you want to delete "${selectedHoliday?.name} holiday"?`}
      />

      <HolidayAddModal
        isOpen={holidayModal}
        selectedYear={selectedYear}
        initialData={editingHoliday}
        onClose={() => setIsHolidayModal(false)}
        onSave={(holiday) => {
          handleHoliday(holiday);
        }}
      />

      <CopyHolidaysModal
        open={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        onConfirm={copyToThisYear}
        year={selectedYear}
      />

      <ExcelUploadModal
        open={excelModal}
        onClose={() => setExcelModal(false)}
        onUpload={(file) => {
          handleExcelUpload(file);
        }}
      />
    </div>
  );
};

const HolidayAddModal = ({
  isOpen,
  onClose,
  selectedYear,
  onSave,
  initialData,
}) => {
  const [holiday, setHoliday] = useState({ name: "", date: "", id: "" });
  useEffect(() => {
    if (initialData) {
      setHoliday({
        name: initialData.name || "",
        date: initialData.date?.slice(0, 10) || "",
        id: initialData._id,
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (!initialData) return;

    if (holiday.date) {
      const year = Number(holiday.date.slice(0, 4));

      // If date does NOT match selected year → adjust it
      if (year !== selectedYear) {
        const [, mm, dd] = holiday.date.split("-");
        setHoliday((prev) => ({
          ...prev,
          date: `${selectedYear}-${mm}-${dd}`,
        }));
      }
    }
  }, [selectedYear]);

  const yearStart = `${selectedYear}-01-01`;
  const yearEnd = `${selectedYear}-12-31`;

  const handleSave = () => {
    if (!holiday.name || !holiday.date) {
      toast.error("Both holiday name and date are required.");
      return;
    }
    onSave(holiday);
    setHoliday({ name: "", date: "" });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900 dark:text-slate-100">
            {initialData ? "Edit Holiday" : "Add Holiday"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 dark:text-slate-400">
            {initialData
              ? "Modify the existing holiday details."
              : "Add a new holiday to your organization's calendar."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Holiday Name */}
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Holiday Name
            </label>
            <Input
              type="text"
              placeholder="Enter holiday name"
              className="mt-1 bg-white text-slate-800 dark:bg-slate-800 dark:text-slate-200"
              value={holiday.name}
              onChange={(e) =>
                setHoliday((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          {/* Holiday Date */}
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Holiday Date
            </label>
            <Input
              type="date"
              className="mt-1 bg-white text-slate-800 dark:bg-slate-800 dark:text-slate-200"
              value={holiday.date}
              min={yearStart}
              max={yearEnd}
              onChange={(e) =>
                setHoliday((prev) => ({ ...prev, date: e.target.value }))
              }
            />
          </div>
        </div>

        <DialogFooter className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            className=" text-white flex items-center gap-2"
          >
            <Save size={16} />
            {initialData ? "Update Holiday" : "Save Holiday"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  destructive = false,
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-800 dark:text-white">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 dark:text-slate-400">
            {description}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="pt-4 flex justify-end gap-3">
          <Button
            variant="outline"
            className="dark:border-slate-700"
            onClick={onClose}
          >
            {cancelText}
          </Button>

          <Button
            onClick={onConfirm}
            className={
              destructive
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-primary hover:bg-[#287a85] text-white"
            }
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CopyHolidaysModal = ({ open, onClose, onConfirm, year }) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-800 dark:text-white">
            Copy Holidays?
          </DialogTitle>

          <DialogDescription className="text-sm text-slate-600 dark:text-slate-400">
            This will copy all holidays from <strong>{year - 1}</strong> to{" "}
            <strong>{year}</strong>.
            <br />
            You can edit or delete them later.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="pt-4 flex justify-end gap-3">
          <Button
            variant="outline"
            className="dark:border-slate-700"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            onClick={onConfirm}
            className="bg-primary hover:bg-primary/80 text-white"
          >
            Copy Holidays
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ExcelUploadModal = ({ open, onClose, onUpload }) => {
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const allowed = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!allowed.includes(selected.type)) {
      toast.error("Only .xls or .xlsx files are allowed!");
      return;
    }
    setFile(selected);
  };

  const handleSubmit = () => {
    if (!file) {
      toast.error("Please select a file first.");
      return;
    }
    onUpload(file);
    setFile(null);
    onClose();
  };

  const removeFile = () => setFile(null);

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/excel-templates/holidayTemplate.xlsx";
    link.download = "holidayTemplate.xlsx";
    link.click();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
        {/* Header */}
        <DialogHeader className="px-6 py-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="text-lg font-bold text-slate-900 dark:text-slate-100">
                Upload Excel File
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-500 dark:text-slate-400">
                Upload your company holidays in bulk using an Excel file.
              </DialogDescription>
              <Button
                type="button"
                variant="outline"
                className="shrink-0 mt-2"
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="px-6 ">
          {!file ? (
            <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-xl cursor-pointer border-slate-300 dark:border-slate-700 hover:border-primary/70 transition-colors">
              <UploadCloud className="w-10 h-10 text-slate-400" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Click to choose an Excel file
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Supported: .xls, .xlsx
              </p>
              <Input
                type="file"
                accept=".xls,.xlsx"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          ) : (
            <div className="flex items-center justify-between gap-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3 min-w-0">
                <FileSpreadsheet className="text-primary shrink-0" size={22} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Ready to upload
                  </p>
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                onClick={removeFile}
              >
                <Trash2 size={18} />
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setFile(null);
              onClose();
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            className="bg-primary text-white"
            onClick={handleSubmit}
            disabled={!file}
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeaveConfiguration;
